import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Popover,
  Typography,
  IconButton,
  Grid,
  Paper,
  CircularProgress,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  parse,
} from 'date-fns';
import { es } from 'date-fns/locale';

function getRangeLabel(start, end, quickRanges, selectedQuickRange) {
  if (
    selectedQuickRange !== null &&
    quickRanges[selectedQuickRange] &&
    quickRanges[selectedQuickRange].label.startsWith('Últimos')
  ) {
    return quickRanges[selectedQuickRange].label;
  }
  if (!start || !end) return 'Selecciona un período';
  return `${format(start, 'dd/MM/yy', { locale: es })} - ${format(
    end,
    'dd/MM/yy',
    { locale: es },
  )}`;
}

function getDaysArray(start, end) {
  const arr = [];
  let dt = start;
  while (dt <= end) {
    arr.push(dt);
    dt = addDays(dt, 1);
  }
  return arr;
}

function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}

function CalendarMonth({
  monthDate,
  selectedStart,
  selectedEnd,
  hoverDate,
  onDayClick,
  onDayHover,
  minDate,
  maxDate,
}) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const days = getDaysArray(monthStart, monthEnd);
  const firstDayOfWeek = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;
  const lastDayOfWeek = monthEnd.getDay() === 0 ? 6 : monthEnd.getDay() - 1;
  const endPadding = 6 - lastDayOfWeek;

  return (
    <Box>
      <Typography align="center" sx={{ fontWeight: 600, mb: 1 }}>
        {format(monthDate, 'MMMM yyyy', { locale: es })}
      </Typography>
      <Grid container spacing={0.5} justifyContent="center">
        {[...Array(7)].map((_, i) => (
          <Grid item xs={1.7} key={i}>
            <Button
              size="small"
              variant="text"
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                color: 'text.secondary',
                fontWeight: 500,
                pointerEvents: 'none',
              }}>
              {format(
                addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i),
                'EEEEE',
                { locale: es },
              )}
            </Button>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={0.5} justifyContent="center">
        {/* Padding inicial para rellenar días de mes anterior */}
        {Array.from({ length: firstDayOfWeek }).map((_, idx) => (
          <Grid item xs={1.7} key={`empty-start-${idx}`}></Grid>
        ))}
        {/* Días del mes */}
        {days.map((day, idx) => {
          const isDisabled =
            (minDate && day < minDate) || (maxDate && day > maxDate);
          const isSelected =
            (selectedStart && isSameDay(day, selectedStart)) ||
            (selectedEnd && isSameDay(day, selectedEnd));
          const inRange =
            isValidDate(selectedStart) &&
            isValidDate(selectedEnd) &&
            isWithinInterval(day, { start: selectedStart, end: selectedEnd });
          const isHovered =
            isValidDate(selectedStart) &&
            isValidDate(hoverDate) &&
            !selectedEnd &&
            isWithinInterval(day, {
              start: selectedStart < hoverDate ? selectedStart : hoverDate,
              end: selectedStart < hoverDate ? hoverDate : selectedStart,
            });
          return (
            <Grid item xs={1.7} key={idx}>
              <Button
                size="small"
                variant={isSelected ? 'contained' : 'text'}
                sx={{
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  bgcolor: isSelected
                    ? 'primary.main'
                    : inRange || isHovered
                    ? 'primary.light'
                    : 'transparent',
                  color: isSelected
                    ? '#fff'
                    : inRange || isHovered
                    ? 'primary.main'
                    : 'inherit',
                  borderRadius: '50%',
                  opacity: isDisabled ? 0.3 : 1,
                  fontWeight: isSelected ? 700 : 400,
                  pointerEvents: isDisabled ? 'none' : 'auto',
                }}
                disabled={isDisabled}
                onClick={() => onDayClick(day)}
                onMouseEnter={() => onDayHover && onDayHover(day)}
                onMouseLeave={() => onDayHover && onDayHover(null)}>
                {format(day, 'd', { locale: es })}
              </Button>
            </Grid>
          );
        })}
        {/* Padding final para rellenar días de mes siguiente */}
        {Array.from({ length: endPadding }).map((_, idx) => (
          <Grid item xs={1.7} key={`empty-end-${idx}`}></Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function DateRangePicker({
  value,
  onChange,
  minDate,
  maxDate,
  quickRanges = [],
  selectedQuickRange = null,
  onQuickRangeChange,
  loading,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState([
    startOfMonth(value?.[0] || new Date()),
    addMonths(startOfMonth(value?.[0] || new Date()), 1),
  ]);
  const [selectedStart, setSelectedStart] = useState(value?.[0] || null);
  const [selectedEnd, setSelectedEnd] = useState(value?.[1] || null);
  const [hoverDate, setHoverDate] = useState(null);

  const inputRef = useRef();

  const handleOpen = e => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setHoverDate(null);
  };

  const handleDayClick = day => {
    if (!selectedStart || (selectedStart && selectedEnd)) {
      setSelectedStart(day);
      setSelectedEnd(null);
      onQuickRangeChange && onQuickRangeChange(null);
    } else if (selectedStart && !selectedEnd) {
      if (day < selectedStart) {
        setSelectedEnd(selectedStart);
        setSelectedStart(day);
      } else {
        setSelectedEnd(day);
      }
      onChange &&
        onChange([
          selectedStart < day ? selectedStart : day,
          selectedStart < day ? day : selectedStart,
        ]);
      setOpen(false);
      onQuickRangeChange && onQuickRangeChange(null);
    }
  };

  const handleDayHover = day => {
    setHoverDate(day);
  };

  const handlePrevMonth = () => {
    setDisplayMonth([subMonths(displayMonth[0], 1), displayMonth[0]]);
  };
  const handleNextMonth = () => {
    setDisplayMonth([displayMonth[1], addMonths(displayMonth[1], 1)]);
  };

  const handleQuickRange = (range, idx) => {
    let start, end;
    
    if (range.type === 'week_to_date' || range.type === 'month_to_date') {
      const dateRange = range.getDateRange();
      start = parse(dateRange.start_date, 'yyyy-MM-dd', new Date());
      end = parse(dateRange.end_date, 'yyyy-MM-dd', new Date());
    } else {
      end = new Date();
      start = addDays(end, -range.days + 1);
    }
    
    setSelectedStart(start);
    setSelectedEnd(end);
    onQuickRangeChange && onQuickRangeChange(idx, range);
    
    const startMonth = startOfMonth(start);
    const endMonth = startOfMonth(end);
    if (format(startMonth, 'yyyy-MM') === format(endMonth, 'yyyy-MM')) {
      setDisplayMonth([startMonth, addMonths(startMonth, 1)]);
    } else {
      setDisplayMonth([startMonth, endMonth]);
    }
    setOpen(false);
  };

  return (
    <Box>
      <Button
        ref={inputRef}
        variant="outlined"
        startIcon={<DateRangeIcon />}
        onClick={handleOpen}
        sx={{
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.05)',
          fontWeight: 500,
          minWidth: 220,
          justifyContent: 'flex-start',
        }}
        disabled={loading}
      >
        {getRangeLabel(selectedStart, selectedEnd, quickRanges, selectedQuickRange)}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2,
            borderRadius: 2,
            width: 1000,
            boxSizing: 'border-box',
          },
        }}>
        <Grid container spacing={2}>
          {/* Quick Ranges */}
          <Grid item xs={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {quickRanges.map((range, idx) => (
                <Button
                  key={idx}
                  variant={selectedQuickRange === idx ? 'contained' : 'text'}
                  color={selectedQuickRange === idx ? 'primary' : 'inherit'}
                  sx={{
                    justifyContent: 'flex-start',
                    color:
                      selectedQuickRange === idx
                        ? 'primary.contrastText'
                        : 'text.secondary',
                    bgcolor:
                      selectedQuickRange === idx
                        ? 'primary.main'
                        : 'transparent',
                  }}
                  onClick={() => handleQuickRange(range, idx)}>
                  {range.label}
                </Button>
              ))}
            </Box>
          </Grid>
          {/* Calendars */}
          <Grid item xs={9}>
            <Grid container xs={12} spacing={2} alignItems="flex-start">
              <Grid item xs={1} alignSelf="center">
                <IconButton onClick={handlePrevMonth}>
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              </Grid>

              <Grid item xs={5}>
                <CalendarMonth
                  monthDate={displayMonth[0]}
                  selectedStart={selectedStart}
                  selectedEnd={selectedEnd}
                  hoverDate={hoverDate}
                  onDayClick={handleDayClick}
                  onDayHover={handleDayHover}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </Grid>

              <Grid item xs={5}>
                <CalendarMonth
                  monthDate={displayMonth[1]}
                  selectedStart={selectedStart}
                  selectedEnd={selectedEnd}
                  hoverDate={hoverDate}
                  onDayClick={handleDayClick}
                  onDayHover={handleDayHover}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </Grid>

              <Grid item xs={1} alignSelf="center">
                <IconButton onClick={handleNextMonth}>
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Popover>
    </Box>
  );
}
